import { Button, Grid, Menu, Text, TextInput } from '@mantine/core';
import { memo, ReactNode, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '../Table';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import { IOrder } from '../../interfaces/Order';
import { MRT_PaginationState, MRT_RowSelectionState } from 'mantine-react-table';
import { useGetOrders } from '../../hooks/orders/queries';
import { useSelector } from 'react-redux';
import { getOrderAmountDue, isApprovedPaymentLine } from '@washday/utilities';
import { getAllPieces } from '../../utils/helpers/getAllPieces';
import { buildOrderFolio } from '../../utils/ordersUtils';

export interface BulkOrderSelection {
  ordersIds: string[];
  storeId: string;
  selectedOrders: IOrder[];
  isSelectionCurrent?: () => boolean;
}

interface UpdateOption {
  id: string;
  label: string;
  onSubmit: (args: BulkOrderSelection) => unknown;
}

export default memo(function BulkUpdateOrderForm({
  storeId = '',
  updateOptions,
  orderStatusFilter,
  assignedEmployee,
  assignedEmployeeLabel
}: {
  close: () => void;
  storeId: string;
  updateOptions: UpdateOption[];
  orderStatusFilter: string;
  assignedEmployee?: string;
  assignedEmployeeLabel?: string;
}) {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'FormValidator',
    'Backend',
    'UpdateOrderForm',
    'OrdersPage'
  ]);
  const hideOrderTotals = useSelector(
    (state: { user: { user?: { permission?: { hideOrderTotals?: { canView?: boolean } } } } }) =>
      state.user.user?.permission?.hideOrderTotals?.canView
  );
  const [allOrders, setAllOrders] = useState<IOrder[]>([]);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const selectionVersion = useRef(0);
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const columns = [
    {
      accessorFn: (row: IOrder) => buildOrderFolio(row?.folioPrefix, row?.sequence),
      header: t('FieldName:order')
    },
    {
      accessorFn: (row: IOrder) =>
        getCustomerFullName(
          typeof row.customer === 'object' ? row.customer.name : '',
          typeof row.customer === 'object' ? row.customer.lastName : ''
        ),
      header: t('FieldName:customer')
    },
    {
      accessorFn: (row: IOrder) => {
        const orderProducts = (row?.products || []).map((product) =>
          typeof product === 'string' ? product : { ...product, pieces: Number(product.pieces) }
        );
        const orderBuyAndGetProducts = (row?.buyAndGetProducts || []).map((product) =>
          typeof product === 'string' ? product : { ...product, pieces: Number(product.pieces) }
        );
        const allPieces = getAllPieces(orderProducts, orderBuyAndGetProducts);
        return allPieces;
      },
      header: t('FieldName:pieces')
    },
    ...(!hideOrderTotals
      ? [
          {
            accessorFn: (row: IOrder) => formatMoneyString((row.total || 0).toFixed(2)),
            header: t('FieldName:total')
          },
          {
            accessorFn: (row: IOrder) =>
              formatMoneyString(
                getOrderAmountDue({
                  ...row,
                  paymentLines: row.paymentLines?.filter(isApprovedPaymentLine)
                }).toFixed(2)
              ),
            header: t('FieldName:amountDue')
          }
        ]
      : [])
  ];
  const limitPerReq = 9;
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    isLoading: isLoadingOrders,
    isError
  } = useGetOrders(!!storeId, {
    pageNum: pagination.pageIndex,
    q: searchTerm,
    status: orderStatusFilter,
    storeId: storeId,
    limit: limitPerReq,
    assignedEmployee
  });

  const scopeKey = JSON.stringify([storeId, orderStatusFilter, assignedEmployee, searchTerm]);
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;

  const clearSelection = () => {
    selectionVersion.current += 1;
    setRowSelection({});
    setAllOrders([]);
  };
  useEffect(() => {
    clearSelection();
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [scopeKey]);

  const updateSearchTerm = (value?: string) => {
    clearSelection();
    setSearchTerm(value || undefined);
  };
  const clearSearchTerm = () => {
    if (searchTermInputRef.current) searchTermInputRef.current.value = '';
    updateSearchTerm();
  };

  // Merge fetched orders into allOrders state
  useEffect(() => {
    if (data?.pages) {
      const newOrders = data.pages.flatMap((page) => page.orders || []);
      setAllOrders((prevOrders) => {
        const orderIds = new Set(prevOrders.map((order) => order._id));
        const uniqueOrders = newOrders.filter((order) => !orderIds.has(order._id));
        return [...prevOrders, ...uniqueOrders];
      });
    }
  }, [data, scopeKey]);

  const orders = useMemo(() => {
    return data?.pages?.reduce((list, { orders }: { orders: IOrder[] }) => {
      list.push(...orders);
      return list;
    }, [] as IOrder[]);
  }, [data]);

  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows[0]?.totalRowsCount ?? 0, [data]);
  const pages = useMemo(() => Math.max(1, Math.ceil(totalRowsCount / limitPerReq)), [totalRowsCount]);

  return (
    <Grid align="center">
      <Grid.Col span={12}>
        <Text size="sm">
          {t('UpdateOrderForm:scopeStatus')}: {t(`FieldName:orderStatuses.${orderStatusFilter}`)}
        </Text>
        <Text size="sm">
          {t('UpdateOrderForm:scopeEmployee')}:{' '}
          {assignedEmployeeLabel || t('OrdersPage:filters.all')}
        </Text>
      </Grid.Col>
      <Grid.Col span={12} my={10}>
        <Grid align="center" justify="center">
          <Grid.Col span={{ xs: 12, md: 10 }}>
            <TextInput
              ref={searchTermInputRef}
              onChange={(event) => updateSearchTerm(event.currentTarget.value)}
              placeholder={`${t('FieldName:order')}, ${t('FieldName:customer')}, ${t(
                'FieldName:phone'
              )}`}
              label={`${t('FieldName:order')}`}
            />
          </Grid.Col>
          <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
            <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
              {t('CommonButtons:clear').toUpperCase()}
            </Button>
          </Grid.Col>
        </Grid>
      </Grid.Col>
      <Grid.Col span={12}>
        <Table
          tableColumns={columns}
          pagination={pagination}
          setPagination={setPagination}
          tableData={orders || []}
          enableRowSelection={true}
          isError={isError}
          paginationIsLoading={isLoadingOrders}
          numPages={pages}
          totalRowsCount={totalRowsCount}
          overrideTableConfigs={{
            getRowId: (originalRow: IOrder) => originalRow._id,
            onRowSelectionChange: (updater: SetStateAction<MRT_RowSelectionState>) => {
              selectionVersion.current += 1;
              setRowSelection(updater);
            },
            // positionToolbarAlertBanner: 'head-overlay', //show alert banner in bottom toolbar instead of top
            state: {
              rowSelection
            },
            renderToolbarAlertBannerContent: ({ selectedAlert }: { selectedAlert: ReactNode }) => {
              return (
                <Grid>
                  <Grid.Col span={3}>{selectedAlert} </Grid.Col>
                  <Grid.Col span={5}>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <Button>{t('UpdateOrderForm:bulkOptions')}</Button>
                      </Menu.Target>

                      <Menu.Dropdown>
                        {updateOptions.map((updateOption) => {
                          return (
                            <Menu.Item
                              onClick={() => {
                                const version = selectionVersion.current;
                                const scope = scopeKey;
                                const ordersIds = Object.keys(rowSelection).filter(
                                  (id) => rowSelection[id]
                                );
                                if (!ordersIds.length) return;
                                updateOption.onSubmit({
                                  ordersIds,
                                  storeId: storeId,
                                  selectedOrders: allOrders.filter((order: IOrder) =>
                                    ordersIds.includes(order._id || '')
                                  ),
                                  isSelectionCurrent: () =>
                                    scopeRef.current === scope &&
                                    selectionVersion.current === version
                                });
                              }}
                              key={updateOption.id}
                            >
                              {updateOption.label}
                            </Menu.Item>
                          );
                        })}
                      </Menu.Dropdown>
                    </Menu>
                  </Grid.Col>
                </Grid>
              );
            }
          }}
        />
      </Grid.Col>
    </Grid>
  );
});
